import { useEffect, useState } from "react";
import { api, errMsg, formatRupiah, formatDate } from "@/lib/api";
import { Check, Link2Off, Search } from "lucide-react";

function Row({ item, projects, onSaved }) {
  const [pid, setPid] = useState(item.project_id || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const tid = item.label_key.toLowerCase().replace(/[^a-z0-9]+/g, "-");

  async function save(projectId) {
    setBusy(true); setErr("");
    try { await api.post("/sheet/mappings", { label_key: item.label_key, project_id: projectId || null }); onSaved(); }
    catch (ex) { setErr(errMsg(ex)); }
    setBusy(false);
  }

  async function remove() {
    setBusy(true);
    try { await api.post("/sheet/mappings/remove", { label_key: item.label_key }); setPid(""); onSaved(); }
    catch (ex) { setErr(errMsg(ex)); }
    setBusy(false);
  }

  const mapped = item.status === "mapped";
  return (
    <div className="p-4 border border-[#E2DDD3] rounded-lg space-y-2" data-testid={`match-row-${tid}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium text-sm break-words">{item.label}</div>
          <div className="text-xs text-[#8A999A]">{item.count} transaksi · keluar {formatRupiah(item.out)}{item.in ? ` · masuk ${formatRupiah(item.in)}` : ""}</div>
        </div>
        <span className={`chip shrink-0 ${mapped ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"}`}>
          {mapped ? (item.project_id ? "Sudah dicocokkan" : "Tanpa project") : "Belum cocok"}
        </span>
      </div>
      <div className="flex gap-2 flex-wrap">
        <select value={pid} onChange={e => setPid(e.target.value)} className="flex-1 min-w-[200px] border border-[#E2DDD3] rounded-lg px-3 h-11 text-sm" data-testid={`match-select-${tid}`}>
          <option value="">— Tanpa project (biaya kantor/umum) —</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}{p.event_date ? ` · ${formatDate(p.event_date)}` : ""}</option>)}
        </select>
        <button disabled={busy} onClick={() => save(pid)} className="btn-primary" data-testid={`match-save-${tid}`}><Check size={16} />Simpan</button>
        {mapped && <button disabled={busy} onClick={remove} className="btn-copper" data-testid={`match-remove-${tid}`}><Link2Off size={16} />Lepas</button>}
      </div>
      {err && <div className="text-xs text-red-700">{err}</div>}
    </div>
  );
}

export default function ProjectMatcher({ refreshKey }) {
  const [data, setData] = useState({ items: [], projects: [] });
  const [q, setQ] = useState("");
  const load = () => api.get("/sheet/unmatched").then(r => setData(r.data));
  useEffect(() => { load(); }, [refreshKey]);

  const items = data.items.filter(i => !q || i.label.toLowerCase().includes(q.toLowerCase()));
  const open = data.items.filter(i => i.status === "unmatched").length;
  return (
    <div className="card-editorial p-5 space-y-4" data-testid="project-matcher">
      <div>
        <h2 className="font-serif-display text-xl font-semibold">Pencocokan Nama Project</h2>
        <p className="text-sm text-[#536263]">Nama project di Petty Cash yang tidak sama dengan Job Summary. Pilih project yang benar. Pilihan ini juga dipakai saat impor dan sinkron berikutnya.</p>
        <p className="text-xs mt-1 text-amber-800" data-testid="match-open-count">{open} nama belum dicocokkan</p>
      </div>
      <div className="relative">
        <Search size={16} className="absolute left-3 top-3.5 text-[#8A999A]" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Cari nama..." className="w-full border border-[#E2DDD3] rounded-lg pl-9 pr-3 h-11" data-testid="match-search" />
      </div>
      <div className="space-y-3">
        {items.map(i => <Row key={`${i.label_key}-${i.status}-${i.project_id}`} item={i} projects={data.projects} onSaved={load} />)}
        {items.length === 0 && <p className="text-sm text-[#8A999A]">Semua nama project sudah cocok.</p>}
      </div>
    </div>
  );
}
